import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import DoubleScrollbar from './DoubleScrollbar';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}

function Table<T extends Record<string, any>>({
  data,
  columns,
  sortColumn,
  sortDirection,
  onSort,
  loading = false,
  emptyMessage = 'No data available',
  className = ''
}: TableProps<T>) {
  const getSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return <ChevronUp className="w-4 h-4 text-[var(--color-text-secondary)]" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[var(--color-primary)]" />
      : <ChevronDown className="w-4 h-4 text-[var(--color-primary)]" />;
  };

  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  if (loading) {
    return (
      <div className={`bg-[var(--color-surface)] rounded-lg shadow-md overflow-hidden ${className}`}>
        <div className="animate-pulse">
          <div className="h-12 bg-[var(--color-background)]"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--color-surface)] border-t border-[var(--color-border)]"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-sm overflow-hidden text-[var(--color-text)] ${className}`}>
      <DoubleScrollbar>
        <table className="w-full">
          <thead className="bg-[var(--color-background)] text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`
                    px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider font-['Times_New_Roman']
                    ${column.sortable ? 'cursor-pointer hover:bg-[var(--color-surface-hover)]' : ''}
                    ${column.width ? `w-${column.width}` : ''}
                  `}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {column.sortable && (
                      <div className="flex flex-col">
                        {getSortIcon(column.key)}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-[var(--color-text)]">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-6 py-12 text-center text-[var(--color-text-secondary)] font-['Times_New_Roman']"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={index}
                  className="hover:bg-[var(--color-background)] transition-colors"
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text)] font-['Times_New_Roman']"
                    >
                      {column.render 
                        ? column.render(item)
                        : item[column.key]
                      }
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DoubleScrollbar>
    </div>
  );
}

export default Table; 